<script lang="ts">
  import { slide } from 'svelte/transition'
  import { onMount, onDestroy } from 'svelte'
  import { unifiedPromptStore, type PromptType } from '$lib/stores/unifiedPrompt'
  import { postingPromptStore } from '$lib/stores/postingPrompt'
  import { systemPromptStore } from '$lib/stores/systemPrompt'
  
  interface Props {
    onHeightChange?: (height: number) => void
  }
  
  let { onHeightChange }: Props = $props()
  
  let state = $state<{ isVisible: boolean; currentType: PromptType }>({ 
    isVisible: false, 
    currentType: 'posting' 
  })
  
  // Derived state for visibility - more reactive
  let isVisible = $derived(state.isVisible)
  
  let promptInput = $state('')
  let textareaElement = $state<HTMLTextAreaElement | null>(null)
  let containerElement = $state<HTMLDivElement | null>(null)
  let generationBarElement = $state<HTMLDivElement | null>(null)
  let charCount = $derived(promptInput.length)
  let isComposing = $state(false)
  
  // Auto-save states
  let lastSavedValue = $state('')
  let saveTimer: NodeJS.Timeout | null = null
  let saveStatus = $state<'idle' | 'saving' | 'saved'>('idle')
  
  // Derived states for UI
  let isSaving = $derived(saveStatus === 'saving')
  let isSaved = $derived(saveStatus === 'saved')
  
  // Get max length based on prompt type
  const maxLength = $derived(state.currentType === 'system' ? systemPromptStore.getMaxLength() : 2000)
  
  // Placeholder examples
  const placeholders = {
    posting: [
      '예시: "이 포스팅은 초보자를 위한 설명이니 쉽게 풀어서 작성해줘." (※ 포스팅 프롬프트가 시스템 프롬프트보다 우선 적용됩니다)',
      '예시: "SEO에 최적화된 블로그 포스팅 형식으로 작성해줘." (※ 시스템 설정과 상충 시 이 설정이 우선합니다)',
      '예시: "기술 문서처럼 정확하고 상세하게 작성해줘." (※ 포스팅별 개별 스타일 설정)',
    ],
    system: [
      '예시: "친근하고 대화체로 작성해줘. 이모티콘은 사용하지 마." (※ 전체 문서의 기본 스타일)',
      '예시: "전문적이고 격식있는 문체로 작성해줘." (※ 포스팅 프롬프트가 설정되면 그것이 우선 적용됩니다)',
      '예시: "다음 문체를 참고해서 작성해줘: [참고할 문장 붙여넣기]" (※ 모든 포스팅의 기본값)',
    ]
  }
  
  let currentPlaceholder = $state('')
  let unsubscribeUnified: (() => void) | null = null
  let unsubscribePosting: (() => void) | null = null
  let unsubscribeSystem: (() => void) | null = null
  
  // Track height changes
  $effect(() => {
    if (!generationBarElement || !onHeightChange) return
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (state.isVisible) {
          const height = entry.borderBoxSize?.[0]?.blockSize || entry.contentRect.height
          onHeightChange(height)
        } else {
          onHeightChange(0)
        }
      }
    })
    
    resizeObserver.observe(generationBarElement)
    
    return () => {
      resizeObserver.disconnect()
    }
  })
  
  // Track visibility changes
  $effect(() => {
    if (onHeightChange) {
      if (!state.isVisible) {
        onHeightChange(0)
      }
    }
  })
  
  // Subscribe to unified store
  onMount(() => {
    unsubscribeUnified = unifiedPromptStore.subscribe(s => {
      // Update state properties individually for better reactivity
      state.isVisible = s.isVisible
      state.currentType = s.currentType
      
      // Load appropriate prompt when type changes
      if (s.isVisible) {
        loadPromptForType(s.currentType)
      }
    })
    
      // Subscribe to posting prompt changes
    unsubscribePosting = postingPromptStore.subscribe(s => {
      if (state.currentType === 'posting' && state.isVisible) {
        const data = s.data
        if (data && data.prompt !== promptInput) {
          promptInput = data.prompt
          lastSavedValue = data.prompt
          saveStatus = 'idle'
          adjustTextareaHeight()
        }
      }
    })
    
    // Subscribe to system prompt changes
    unsubscribeSystem = systemPromptStore.subscribe(s => {
      if (state.currentType === 'system' && state.isVisible) {
        const prompt = systemPromptStore.getPrompt()
        if (prompt !== promptInput) {
          promptInput = prompt
          lastSavedValue = prompt
          saveStatus = 'idle'
          adjustTextareaHeight()
        }
      }
    })
    
    // Set initial placeholder
    const plArray = placeholders[state.currentType]
    currentPlaceholder = plArray[Math.floor(Math.random() * plArray.length)]
  })
  
  // Global ESC key handler - only for UnifiedPromptPanel
  $effect(() => {
    if (!state.isVisible) return
    
    const handleGlobalEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Check if this is actually our prompt panel (not GenerationBar)
        const unifiedPanel = document.querySelector('.unified-prompt-panel')
        if (unifiedPanel) {
            e.preventDefault()
          e.stopPropagation()
          handleClose()
        }
      }
    }
    
    // Attach listener when panel is visible
    window.addEventListener('keydown', handleGlobalEscape, true)
    
    return () => {
      window.removeEventListener('keydown', handleGlobalEscape, true)
    }
  })
  
  onDestroy(() => {
    if (unsubscribeUnified) unsubscribeUnified()
    if (unsubscribePosting) unsubscribePosting()
    if (unsubscribeSystem) unsubscribeSystem()
    if (saveTimer) clearTimeout(saveTimer)
  })
  
  function loadPromptForType(type: PromptType) {
    if (type === 'posting') {
      const data = postingPromptStore.get()
      promptInput = data.prompt
      lastSavedValue = data.prompt
    } else {
      const prompt = systemPromptStore.getPrompt()
      promptInput = prompt
      lastSavedValue = prompt
    }
    saveStatus = 'idle'
    
    // Update placeholder
    const plArray = placeholders[type]
    currentPlaceholder = plArray[Math.floor(Math.random() * plArray.length)]
    
    // Focus on textarea
    setTimeout(() => {
      if (textareaElement) {
        textareaElement.focus()
        textareaElement.setSelectionRange(promptInput.length, promptInput.length)
        adjustTextareaHeight()
      }
    }, 100)
  }
  
  function handleTypeChange(e: Event) {
    const select = e.target as HTMLSelectElement
    const newType = select.value as PromptType
    unifiedPromptStore.setType(newType)
    loadPromptForType(newType)
  }
  
  function handleClose() {
    unifiedPromptStore.hide()
    
    // Focus back to the editor after the panel animates out
    setTimeout(() => {
      // Try multiple selectors to find the editor
      const wysiwygEditor = document.querySelector('.ProseMirror') as HTMLElement
      const sourceTextarea = document.querySelector('textarea.base-editor') as HTMLTextAreaElement
      const fallbackTextarea = document.querySelector('textarea') as HTMLTextAreaElement
      
      if (wysiwygEditor && wysiwygEditor.offsetParent !== null) {
        // WYSIWYG mode - focus ProseMirror editor
        wysiwygEditor.focus()
      } else if (sourceTextarea) {
        // Source mode - focus textarea (BaseEditor)
        sourceTextarea.focus()
      } else if (fallbackTextarea) {
        // Fallback to any textarea
        fallbackTextarea.focus()
      }
    }, 200) // Wait for slide animation to complete
  }
  
  function handleSave() {
    if (state.currentType === 'posting') {
      postingPromptStore.updatePrompt(promptInput)
    } else {
      systemPromptStore.setPrompt(promptInput)
    }
    lastSavedValue = promptInput
    saveStatus = 'idle'
    window.dispatchEvent(new CustomEvent('status-message', { 
      detail: { 
        message: `${state.currentType === 'posting' ? '포스팅' : '시스템'} 프롬프트가 저장되었습니다`, 
        type: 'success' 
      } 
    }))
    handleClose()
  }
  
  function handleClear() {
    promptInput = ''
    lastSavedValue = ''
    saveStatus = 'idle'
    if (state.currentType === 'posting') {
      postingPromptStore.updatePrompt('')
    } else {
      systemPromptStore.clear()
    }
    window.dispatchEvent(new CustomEvent('status-message', { 
      detail: { 
        message: `${state.currentType === 'posting' ? '포스팅' : '시스템'} 프롬프트가 초기화되었습니다`, 
        type: 'info' 
      } 
    }))
    if (textareaElement) {
      textareaElement.focus()
    }
  }
  
  function handleKeyDown(e: KeyboardEvent) {
    
    if (e.metaKey || e.ctrlKey) {
      if (['c', 'v', 'x', 'a', 'z'].includes(e.key.toLowerCase())) {
        return
      }
    }
    
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      handleClose()
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      if (!isComposing) {
        e.preventDefault()
        handleSave()
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      if (!isComposing) {
        e.preventDefault()
        handleSave()
      }
    }
  }
  
  // Auto-save effect
  $effect(() => {
    const currentValue = promptInput
    
    if (isComposing || currentValue === lastSavedValue) return
    
    if (saveTimer) {
      clearTimeout(saveTimer)
    }
    
    saveStatus = 'saving'
    
    saveTimer = setTimeout(() => {
      if (state.currentType === 'posting') {
        postingPromptStore.updatePrompt(currentValue)
      } else {
        systemPromptStore.setPrompt(currentValue)
      }
      lastSavedValue = currentValue
      
      if (currentValue === '') {
        saveStatus = 'saved'
        setTimeout(() => {
          saveStatus = 'idle'
        }, 1000)
      } else {
        saveStatus = 'saved'
        setTimeout(() => {
          saveStatus = 'idle'
        }, 2000)
      }
    }, 800)
  })
  
  function handleInput(e: Event) {
    const target = e.target as HTMLTextAreaElement
    if (target.value.length > maxLength) {
      promptInput = target.value.slice(0, maxLength)
    }
    adjustTextareaHeight()
  }
  
  function adjustTextareaHeight() {
    if (!textareaElement) return
    textareaElement.style.height = 'auto'
    requestAnimationFrame(() => {
      if (textareaElement) {
        textareaElement.style.height = Math.min(textareaElement.scrollHeight, 120) + 'px'
      }
    })
  }
  
  function handleCompositionStart() {
    isComposing = true
  }
  
  function handleCompositionEnd() {
    isComposing = false
  }
  
  // Handle click outside to close
  $effect(() => {
    if (!state.isVisible) return
    
    let isSelecting = false
    
    function handleMouseDown(e: MouseEvent) {
      const target = e.target as Node
      if (containerElement && containerElement.contains(target)) {
        isSelecting = true
        return
      }
    }
    
    function handleMouseUp(e: MouseEvent) {
      if (isSelecting) {
        isSelecting = false
        return
      }
      
      const target = e.target as Node
      if (containerElement && !containerElement.contains(target)) {
        const toolbarButton = (e.target as Element)?.closest('.toolbar-btn')
        if (!toolbarButton || !toolbarButton.getAttribute('aria-label')?.includes('프롬프트')) {
          handleClose()
        }
      }
    }
    
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleMouseDown)
      document.addEventListener('mouseup', handleMouseUp)
    }, 100)
    
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  })
</script>

{#if isVisible}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div 
    class="generation-bar unified-prompt-panel"
    bind:this={generationBarElement}
    transition:slide={{ duration: 150, axis: 'y' }}
    onkeydown={handleKeyDown}
  >
    <div class="chat-container" bind:this={containerElement}>
      <!-- Main textarea -->
      <textarea
        bind:this={textareaElement}
        bind:value={promptInput}
        oninput={handleInput}
        onkeydown={handleKeyDown}
        oncompositionstart={handleCompositionStart}
        oncompositionend={handleCompositionEnd}
        placeholder={currentPlaceholder}
        maxlength={maxLength}
        class="prompt-textarea prompt-input"
        aria-label={state.currentType === 'posting' ? '포스팅 프롬프트' : '시스템 프롬프트'}
        rows="1"
      ></textarea>
    
      <!-- Bottom controls -->
      <div class="bottom-controls">
        <div class="left-controls">
          <!-- Type selector dropdown -->
          <div class="dropdown-wrapper">
            <select 
              value={state.currentType} 
              onchange={handleTypeChange}
              class="type-dropdown"
              aria-label="프롬프트 종류"
            >
              <option value="posting">포스팅 프롬프트</option>
              <option value="system">시스템 프롬프트</option>
            </select>
            <div class="prompt-description">
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="8" cy="8" r="7"/>
                <path d="M8 12v-1m0-3v-3" stroke-linecap="round"/>
              </svg>
              <span>
                {state.currentType === 'posting' ? '해당 문서에만 적용되는 프롬프트입니다.' : '모든 문서의 기본 프롬프트입니다.'}
              </span>
            </div>
          </div>
        </div>
        
        <div class="right-controls">
          {#if promptInput.length >= 300}
            <span class="char-count">{promptInput.length}/{maxLength}</span>
          {/if}
          
          {#if isSaving}
            <span class="save-status saving">저장 중...</span>
          {:else if isSaved}
            <span class="save-status saved">✓ 저장됨</span>
          {/if}
          
          <button 
            class="send-btn" 
            class:saved={isSaved}
            onclick={handleSave}
            disabled={promptInput.length === 0}
            title={isSaved ? "저장됨" : "저장 (Enter)"}
            aria-label="저장"
          >
            {#if isSaved}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12l5 5L20 7" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            {:else}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12l5 5L20 7" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            {/if}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .generation-bar {
    padding: 12px;
    background: transparent;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
  }
  
  .chat-container {
    position: relative;
    background: var(--bg-primary, white);
    border: 1px solid var(--border-primary, #e5e7eb);
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.2s;
    box-sizing: border-box;
    width: 100%;
  }
  
  .chat-container:focus-within {
    border-color: var(--border-secondary, #d1d5db);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
  
  .prompt-textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 4px 0;
    padding-bottom: 36px;
    font-size: 14px;
    line-height: 1.5;
    border: none;
    background: transparent;
    color: var(--text-primary);
    outline: none;
    resize: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    min-height: 24px;
    max-height: 100px;
    overflow-y: auto;
  }
  
  .prompt-textarea::placeholder {
    color: var(--text-tertiary);
    font-style: italic;
    font-size: 12px;
  }
  
  .prompt-textarea::-webkit-scrollbar {
    width: 6px;
  }
  
  .prompt-textarea::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .prompt-textarea::-webkit-scrollbar-thumb {
    background: var(--border-primary);
    border-radius: 3px;
  }
  
  /* Bottom controls */
  .bottom-controls {
    position: absolute;
    bottom: 8px;
    left: 12px;
    right: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .left-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .right-controls {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  /* Dropdown wrapper */
  .dropdown-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  /* Type dropdown */
  .type-dropdown {
    padding: 3px 6px 3px 8px;
    font-size: 11px;
    font-weight: 500;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    background: transparent;
    color: var(--text-secondary, #6b7280);
    border: none;
    cursor: pointer;
    transition: all 0.15s;
    min-width: fit-content;
  }
  
  .type-dropdown:hover {
    background: var(--bg-secondary, #f9fafb);
    color: var(--text-primary, #1f2937);
    border-radius: 4px;
  }
  
  .type-dropdown:focus {
    outline: none;
    background: var(--bg-secondary, #f9fafb);
    color: var(--text-primary, #1f2937);
    border-radius: 4px;
  }
  
  .type-dropdown:focus-visible {
    outline: none;
  }
  
  /* Prompt description */
  .prompt-description {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    color: var(--text-tertiary, #9ca3af);
    white-space: nowrap;
  }
  
  .prompt-description svg {
    flex-shrink: 0;
    opacity: 0.6;
  }
  
  /* Character count */
  .char-count {
    font-size: 11px;
    color: var(--text-tertiary);
    font-family: 'SF Mono', Monaco, monospace;
    opacity: 0.7;
    margin-right: 8px;
  }
  
  /* Save status */
  .save-status {
    font-size: 11px;
    font-weight: 500;
    margin-right: 8px;
    transition: all 0.2s;
  }
  
  .save-status.saving {
    color: var(--text-tertiary);
    opacity: 0.7;
  }
  
  .save-status.saved {
    color: var(--success, #10b981);
  }
  
  /* Send button */
  .send-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    background: var(--accent-primary, #6366f1);
    border: none;
    border-radius: 6px;
    color: white;
    cursor: pointer;
    transition: all 0.15s;
  }
  
  .send-btn.saved {
    background: var(--success, #10b981);
  }
  
  .send-btn:hover:not(:disabled) {
    background: var(--accent-secondary, #5558e3);
    transform: scale(1.05);
  }
  
  .send-btn.saved:hover:not(:disabled) {
    background: var(--success-hover, #0ea968);
  }
  
  .send-btn:disabled {
    background: var(--border-primary, #e5e7eb);
    color: var(--text-tertiary, #9ca3af);
    cursor: not-allowed;
  }
  
  /* Dark theme */
  :global(.dark-theme) .chat-container:focus-within {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  }
  
  :global(.dark-theme) .prompt-textarea::-webkit-scrollbar-thumb {
    background: var(--border-secondary);
  }
  
  :global(.dark-theme) .type-dropdown {
    background: transparent;
    color: var(--text-secondary);
    border: none;
  }
  
  :global(.dark-theme) .type-dropdown:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
    border-radius: 4px;
  }
  
  :global(.dark-theme) .type-dropdown:focus {
    background: var(--bg-secondary);
    color: var(--text-primary);
    border-radius: 4px;
  }
  
  :global(.dark-theme) .prompt-description {
    color: var(--text-tertiary);
  }
  
  :global(.dark-theme) .send-btn:disabled {
    background: var(--bg-tertiary);
    color: var(--text-tertiary);
  }
</style>
